import Parse from 'parse'

import type { BackgroundEffect } from '../services/liveKitVideoBackground'

type DurableRead = { effect: BackgroundEffect; userId: string } | null
type DurableOutcome = { failed: boolean; read: DurableRead }

/**
 * The connector reads the saved background twice on a normal page load: once at mount and once
 * when the store learns who is signed in (SC-452). Both are the same `Profile.first`, so the
 * second one reuses the first answer.
 *
 * The answer is reused only while nothing could have changed it: same Parse session (a new
 * sign-in, which SC-452 rereads for, gets a new token), no background pick since (a pick saves a
 * newer value than the one read), and the read did not fail. A failed read (dead session,
 * network) is never reused: the caller that joined it reads again, so the reread stays a real
 * second attempt. "Nothing saved" is a successful answer and is reused.
 */
export function createDurableBackgroundEffectLoader(deps: {
  load: () => Promise<DurableOutcome>
  /** Identifies the signed-in Parse session, or null when there is none yet. */
  sessionKey: () => string | null
  /** The lifecycle's `pickCount`. */
  pickCount: () => number
}): () => Promise<DurableRead> {
  let last: { key: string; picks: number; outcome: Promise<DurableOutcome> } | null = null

  const get = (): { outcome: Promise<DurableOutcome>; started: boolean } => {
    const key = deps.sessionKey()
    const picks = deps.pickCount()
    if (key && last && last.key === key && last.picks === picks) {
      return { outcome: last.outcome, started: false }
    }

    const outcome = deps.load()
    last = key ? { key, picks, outcome } : null
    // Registered before any caller awaits it, so a failure is forgotten before they continue.
    void outcome.then((o) => {
      if (o.failed && last?.outcome === outcome) last = null
    })
    return { outcome, started: true }
  }

  return async () => {
    const first = get()
    const result = await first.outcome
    if (!result.failed || first.started) return result.read
    return (await get().outcome).read
  }
}

export function currentParseSessionKey(): string | null {
  try {
    const user = Parse.User.current()
    const token = user?.getSessionToken?.()
    return user && token ? `${user.id}:${token}` : null
  } catch {
    return null
  }
}
